import { useContext } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';

import { NotificationContext, useModals } from 'context';
import { getPartner } from 'services/bff';
import { updateEnabledRecommendation } from 'services/nvPartners';

import { Tooltip } from 'components';
import { ConfirmComponentProps } from 'modals/Confirm';

import * as S from './styles';

const HideCPCard: React.FC = () => {
  const intl = useIntl();
  const { openModal } = useModals();
  const { setNotification } = useContext(NotificationContext);

  const {
    data: partner,
    refetch: reloadGetPartner,
    isFetching: isFetchingGetPartner,
  } = useQuery({
    queryKey: ['getPartner'],
    queryFn: getPartner,
  });

  const { mutate } = useMutation({
    mutationFn: updateEnabledRecommendation,
    onSuccess: () => reloadGetPartner(),
    onError: () =>
      setNotification({
        message: intl.formatMessage({
          id: 'ERROR.TRY',
        }),
        type: 'failure',
      }),
  });

  const enableRecommendation = partner?.enableRecommendation;

  const canToggleEnableRecommendation = partner?.canToggleEnableRecommendation;

  const updateEnableRecommendation = () => {
    mutate(!enableRecommendation);
  };

  const handleToggleClick = () => {
    openModal<ConfirmComponentProps>('confirm', {
      props: {
        message: enableRecommendation
          ? 'HIDE_CP.CONFIRM_MODAL.HIDE.MESSAGE'
          : 'HIDE_CP.CONFIRM_MODAL.SHOW.MESSAGE',
        confirmLabel: enableRecommendation
          ? 'HIDE_CP.CONFIRM_MODAL.HIDE.BUTTON'
          : 'HIDE_CP.CONFIRM_MODAL.SHOW.BUTTON',
        onConfirm: () => updateEnableRecommendation(),
      },
    });
  };

  return (
    <S.StyledCard>
      <S.TextContainer>
        <S.CardHeader
          disabled={!canToggleEnableRecommendation}
          data-testid="hideCPHeader"
        >
          <FormattedMessage id="HIDE_CP.CARD.TITLE" />
        </S.CardHeader>
        <S.CardContent
          disabled={!canToggleEnableRecommendation}
          data-testid="hideCPContent"
        >
          <FormattedMessage id="HIDE_CP.CARD.CONTENT" />
        </S.CardContent>
      </S.TextContainer>
      {!isFetchingGetPartner && (
        <Tooltip
          width="initial"
          position="left-center"
          description="HIDE_CP.TIP"
          available={!canToggleEnableRecommendation}
        >
          <S.StyledSwitch
            data-testid="hideCPToggle"
            onChange={handleToggleClick}
            disabled={!canToggleEnableRecommendation}
            checked={!enableRecommendation}
          />
        </Tooltip>
      )}
    </S.StyledCard>
  );
};

export default HideCPCard;
